import { useCallback, useEffect, useState } from "react";
import { apiFetch, useApp } from "../../context/AppContext";
import { Button, Input, Modal, Select } from "./UI";
import CollapsibleSettingsCard from "./CollapsibleSettingsCard";
import {
  Bot,
  CheckCircle2,
  Edit2,
  Eye,
  EyeOff,
  Loader2,
  Plus,
  Trash2,
  Zap,
  XCircle,
} from "lucide-react";
import { confirmAction } from "../../utils/sweetAlert";

const API_BASE = "/tenant";

const PROVIDER_TYPES = [
  { value: "openai", label: "OpenAI" },
  { value: "anthropic", label: "Anthropic Claude" },
  { value: "groq", label: "Groq" },
  { value: "gemini", label: "Google Gemini" },
  { value: "azure_openai", label: "Azure OpenAI" },
  { value: "ollama", label: "Ollama" },
  { value: "openai_compatible", label: "OpenAI-compatible" },
];

const ENDPOINT_HINTS = {
  openai: "https://api.openai.com/v1",
  anthropic: "https://api.anthropic.com/v1",
  groq: "https://api.groq.com/openai/v1",
  gemini: "https://generativelanguage.googleapis.com/v1beta",
  azure_openai: "https://{resource}.openai.azure.com/openai/deployments/{deployment}",
  ollama: "http://localhost:11434",
  openai_compatible: "https://your-api.example.com/v1",
};

const EMPTY_FORM = {
  provider_name: "",
  provider_type: "openai",
  endpoint: ENDPOINT_HINTS.openai,
  api_key: "",
  is_active: false,
  token_quota: 50000,
  alert_percent: 80,
};

function formatProviderType(type) {
  return PROVIDER_TYPES.find((p) => p.value === type)?.label || type;
}

function parseAlertPercent(value, fallback = 80) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  const rounded = Math.round(n);
  if (rounded < 1 || rounded > 100) return fallback;
  return rounded;
}

function parseTokenQuota(value, fallback = 50000) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) return fallback;
  return Math.round(n);
}

export default function AiProviderSettingsPanel() {
  const { showNotification } = useApp();
  const [providers, setProviders] = useState([]);
  const [tokenQuota, setTokenQuota] = useState(50000);
  const [alertPercent, setAlertPercent] = useState(80);
  const [budgetForm, setBudgetForm] = useState({ token_quota: 50000, alert_percent: 80 });
  const [budgetErrors, setBudgetErrors] = useState({});
  const [savingBudget, setSavingBudget] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [revealedIds, setRevealedIds] = useState({});
  const [testModal, setTestModal] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const applyBudget = useCallback((quotaValue, percentValue) => {
    const quota = parseTokenQuota(quotaValue, 50000);
    const percent = parseAlertPercent(percentValue, 80);
    setTokenQuota(quota);
    setAlertPercent(percent);
    setBudgetForm({ token_quota: quota, alert_percent: percent });
    return { quota, percent };
  }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/ai-providers`);
      if (!res.ok) throw new Error("Failed to load AI providers");
      const data = await res.json();
      setProviders(data.providers || []);
      let quota = data.token_quota;
      let percent = data.alert_percent;
      if (percent == null || percent === "") {
        const tokenRes = await apiFetch(`${API_BASE}/settings/ai-tokens`);
        if (tokenRes.ok) {
          const tokenData = await tokenRes.json();
          quota = tokenData.quota ?? quota;
          percent = tokenData.alert_percent;
        }
      }
      applyBudget(quota, percent);
    } catch (e) {
      showNotification(e.message || "Failed to load AI settings", "error");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [applyBudget, showNotification]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...EMPTY_FORM,
      endpoint: ENDPOINT_HINTS.openai,
      token_quota: tokenQuota,
      alert_percent: alertPercent,
    });
    setErrors({});
    setShowKey(false);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      provider_name: row.provider_name,
      provider_type: row.provider_type,
      endpoint: row.endpoint,
      api_key: "",
      is_active: row.is_active,
      token_quota: tokenQuota,
      alert_percent: alertPercent,
    });
    setErrors({});
    setShowKey(false);
    setFormOpen(true);
  };

  const validateForm = () => {
    const next = {};
    if (!form.provider_name.trim()) next.provider_name = "Name is required";
    if (!form.provider_type) next.provider_type = "Provider type is required";
    if (!form.endpoint.trim()) next.endpoint = "Endpoint URL is required";
    else if (!/^https?:\/\/.+/i.test(form.endpoint.trim())) {
      next.endpoint = "Enter a valid http(s) URL";
    }
    if (!editing && !form.api_key.trim()) next.api_key = "API key is required";
    const quota = Number(form.token_quota);
    if (!Number.isInteger(quota) || quota < 1) next.token_quota = "Enter a token limit of at least 1";
    const percent = Number(form.alert_percent);
    if (!Number.isInteger(percent) || percent < 1 || percent > 100) {
      next.alert_percent = "Enter a percentage between 1 and 100";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const body = {
        provider_name: form.provider_name.trim(),
        provider_type: form.provider_type,
        endpoint: form.endpoint.trim(),
        is_active: form.is_active,
        token_quota: Number(form.token_quota),
        alert_percent: Number(form.alert_percent),
      };
      if (form.api_key.trim()) body.api_key = form.api_key.trim();

      const url = editing
        ? `${API_BASE}/ai-providers/${editing.id}`
        : `${API_BASE}/ai-providers`;
      const res = await apiFetch(url, {
        method: editing ? "PATCH" : "POST",
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Save failed");
      showNotification(editing ? "Provider updated" : "Provider added", "success");
      setFormOpen(false);
      await load();
    } catch (e) {
      showNotification(e.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const setBudgetField = (key, value) => {
    setBudgetForm((f) => ({ ...f, [key]: value }));
    setBudgetErrors((e) => ({ ...e, [key]: undefined }));
  };

  const handleSaveBudget = async () => {
    const next = {};
    const quota = Number(budgetForm.token_quota);
    if (!Number.isInteger(quota) || quota < 1) next.token_quota = "Enter a token limit of at least 1";
    const percent = Number(budgetForm.alert_percent);
    if (!Number.isInteger(percent) || percent < 1 || percent > 100) {
      next.alert_percent = "Enter a percentage between 1 and 100";
    }
    setBudgetErrors(next);
    if (Object.keys(next).length) return;
    setSavingBudget(true);
    try {
      const res = await apiFetch(`${API_BASE}/settings/ai-tokens`, {
        method: "PATCH",
        body: JSON.stringify({
          token_quota: quota,
          alert_percent: percent,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Failed to save token settings");
      applyBudget(data.quota ?? quota, data.alert_percent ?? percent);
      showNotification("Token usage settings saved", "success");
      await load({ silent: true });
    } catch (e) {
      showNotification(e.message || "Failed to save token settings", "error");
    } finally {
      setSavingBudget(false);
    }
  };

  const handleDelete = async (row) => {
    const ok = await confirmAction({
      title: "Delete AI provider?",
      text: `Remove "${row.provider_name}" from your configuration.`,
      confirmText: "Delete",
      icon: "warning",
    });
    if (!ok) return;
    try {
      const res = await apiFetch(`${API_BASE}/ai-providers/${row.id}`, { method: "DELETE" });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Delete failed");
      }
      showNotification("Provider deleted", "success");
      await load();
    } catch (e) {
      showNotification(e.message || "Delete failed", "error");
    }
  };

  const handleActivate = async (row) => {
    try {
      const res = await apiFetch(`${API_BASE}/ai-providers/${row.id}/activate`, { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Activation failed");
      showNotification(`${row.provider_name} is now active`, "success");
      await load();
    } catch (e) {
      showNotification(e.message || "Activation failed", "error");
    }
  };

  const runTest = async (row) => {
    setTestModal(row);
    setTestResult(null);
    setTesting(true);
    try {
      const res = await apiFetch(`${API_BASE}/ai-providers/test`, {
        method: "POST",
        body: JSON.stringify({
          endpoint: row.endpoint,
          provider_type: row.provider_type,
          provider_id: row.id,
        }),
      });
      const data = await res.json().catch(() => ({}));
      setTestResult({
        success: data.success,
        message: data.message || data.detail || "Test completed",
        error: data.error,
        at: new Date().toLocaleString(),
      });
    } catch (e) {
      setTestResult({
        success: false,
        message: e.message || "Test failed",
        at: new Date().toLocaleString(),
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <>
    <CollapsibleSettingsCard
      tourId="tour-tenant-settings-ai"
      title="AI Provider Configuration"
      description="Connect OpenAI, Anthropic, Groq, Gemini, Azure, or Ollama. API keys are encrypted and never shown in full."
      icon={<Bot size={18} className="text-white" />}
      iconClassName="bg-gradient-to-br from-violet-500 to-indigo-600"
      headerRight={
        <Button size="sm" onClick={openCreate} className="flex items-center gap-1.5 shrink-0">
          <Plus size={14} />
          Add provider
        </Button>
      }
    >
      {!loading && (
        <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50/80 p-4 space-y-3">
          <div>
            <p className="text-sm font-semibold text-slate-800">Token usage alerts</p>
            <p className="text-xs text-slate-500 mt-0.5">
              The organisation admin is emailed once when usage reaches the configured percentage of the token limit. Repeat alerts are not sent for the same usage period.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              label="Token limit"
              type="number"
              min={1}
              max={10000000}
              value={budgetForm.token_quota}
              onChange={(e) => setBudgetField("token_quota", e.target.value)}
              error={budgetErrors.token_quota}
              hint="Organisation quota shown to users. Default is 50,000."
            />
            <Input
              label="Maximum Token Usage Percentage"
              type="number"
              min={1}
              max={100}
              value={budgetForm.alert_percent}
              onChange={(e) => setBudgetField("alert_percent", e.target.value)}
              error={budgetErrors.alert_percent}
              hint="Alert when used tokens reach this percent of the token limit."
            />
          </div>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-slate-500">
              Current quota{" "}
              <span className="font-semibold text-slate-800 tabular-nums">{tokenQuota.toLocaleString("en-IN")}</span>
              {" "}· alert at{" "}
              <span className="font-semibold text-slate-800 tabular-nums">{alertPercent}%</span>
            </p>
            <Button size="sm" onClick={handleSaveBudget} loading={savingBudget}>
              Save token settings
            </Button>
          </div>
        </div>
      )}
      {loading ? (
        <div className="animate-pulse space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 bg-slate-100 rounded-xl" />
          ))}
        </div>
      ) : providers.length === 0 ? (
        <div className="text-center py-8 rounded-xl border border-dashed border-slate-200 bg-slate-50/80">
          <Bot size={32} className="mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-600 font-medium">No AI providers configured</p>
          <p className="text-xs text-slate-400 mt-1">Add a provider to enable AI features for your organization.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {providers.map((row) => (
            <div
              key={row.id}
              className={`rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${
                row.is_active ? "border-indigo-200 bg-indigo-50/50" : "border-slate-200 bg-white"
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-bold text-slate-900">{row.provider_name}</p>
                  {row.is_active && (
                    <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-indigo-600 text-white">
                      Active
                    </span>
                  )}
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                    {formatProviderType(row.provider_type)}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1 truncate font-mono">{row.endpoint}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs font-mono text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                    {row.api_key_preview}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setRevealedIds((prev) => ({ ...prev, [row.id]: !prev[row.id] }))
                    }
                    className="p-1 rounded text-slate-400 hover:text-slate-600"
                    title="Toggle key preview"
                  >
                    {revealedIds[row.id] ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                {!row.is_active && (
                  <Button size="sm" variant="secondary" onClick={() => handleActivate(row)}>
                    Activate
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => runTest(row)}
                  className="flex items-center gap-1"
                >
                  <Zap size={13} />
                  Test
                </Button>
                <button
                  type="button"
                  onClick={() => openEdit(row)}
                  className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-indigo-600"
                  title="Edit"
                >
                  <Edit2 size={15} />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(row)}
                  className="p-2 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"
                  title="Delete"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </CollapsibleSettingsCard>

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing ? "Edit AI provider" : "Add AI provider"}
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="Provider name"
            value={form.provider_name}
            onChange={(e) => setField("provider_name", e.target.value)}
            placeholder="My OpenAI"
            error={errors.provider_name}
          />
          <Select
            label="Provider type"
            value={form.provider_type}
            onChange={(e) => {
              const type = e.target.value;
              setField("provider_type", type);
              if (!editing && ENDPOINT_HINTS[type]) setField("endpoint", ENDPOINT_HINTS[type]);
            }}
            error={errors.provider_type}
          >
            {PROVIDER_TYPES.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Select>
          <Input
            label="API endpoint"
            value={form.endpoint}
            onChange={(e) => setField("endpoint", e.target.value)}
            placeholder="https://api.openai.com/v1"
            error={errors.endpoint}
          />
          <div>
            <Input
              label="API key"
              type={showKey ? "text" : "password"}
              value={form.api_key}
              onChange={(e) => setField("api_key", e.target.value)}
              placeholder={editing ? "Leave blank to keep existing key" : "sk-..."}
              error={errors.api_key}
              hint={editing ? "Leave blank to keep the existing encrypted key" : undefined}
            />
            <button
              type="button"
              onClick={() => setShowKey((v) => !v)}
              className="mt-1 text-xs text-indigo-600 font-medium flex items-center gap-1"
            >
              {showKey ? <EyeOff size={12} /> : <Eye size={12} />}
              {showKey ? "Hide" : "Show"} key while typing
            </button>
          </div>
          <Input
            label="Token limit"
            type="number"
            min={1}
            max={10000000}
            value={form.token_quota}
            onChange={(e) => setField("token_quota", e.target.value)}
            error={errors.token_quota}
            hint="Organisation quota shown to users. Default is 50,000."
          />
          <Input
            label="Maximum Token Usage Percentage"
            type="number"
            min={1}
            max={100}
            value={form.alert_percent}
            onChange={(e) => setField("alert_percent", e.target.value)}
            error={errors.alert_percent}
            hint="Email the organisation admin once when usage reaches this percent of the token limit."
          />
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setField("is_active", e.target.checked)}
              className="rounded border-slate-300 text-indigo-600"
            />
            <span className="text-sm text-slate-700">Set as active provider</span>
          </label>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button variant="secondary" onClick={() => setFormOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={saving}>
              {editing ? "Save changes" : "Add provider"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={Boolean(testModal)}
        onClose={() => setTestModal(null)}
        title="Test connection"
        size="sm"
      >
        {testModal && (
          <div className="space-y-4">
            <div className="text-sm text-slate-600">
              <p className="font-semibold text-slate-800">{testModal.provider_name}</p>
              <p className="text-xs font-mono mt-1">{testModal.endpoint}</p>
              <p className="text-xs mt-1">{formatProviderType(testModal.provider_type)}</p>
            </div>
            {testing && (
              <div className="flex items-center gap-2 text-sm text-slate-600 py-4">
                <Loader2 size={18} className="animate-spin text-indigo-500" />
                Testing connection…
              </div>
            )}
            {testResult && !testing && (
              <div
                className={`rounded-xl p-4 border ${
                  testResult.success
                    ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                    : "bg-red-50 border-red-200 text-red-800"
                }`}
              >
                <div className="flex items-start gap-2">
                  {testResult.success ? (
                    <CheckCircle2 size={18} className="shrink-0 mt-0.5" />
                  ) : (
                    <XCircle size={18} className="shrink-0 mt-0.5" />
                  )}
                  <div>
                    <p className="text-sm font-semibold">{testResult.message}</p>
                    <p className="text-[10px] mt-1 opacity-70">{testResult.at}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
